/**
 * Admin Feature - Core API Service
 *
 * Camada de comunicação com a API do admin
 * Usa a configuração base da aplicação
 */
import { api, API_BASE_URL } from '../../../app/shared/utils/api'

/**
 * Helper para fazer requests com baseURL /admin
 */
const adminRequest = async (method, url, data = null, config = {}) => {
	// Remove data se for null/undefined para evitar erro de parse no DELETE
	const requestConfig = {
		method,
		url: `/admin${url}`,
		...config,
	}

	// Só adiciona data se não for null/undefined
	if (data !== null && data !== undefined) {
		requestConfig.data = data
	}

	const response = await api(requestConfig)
	return response
}

// =====================================================
// AUTH
// =====================================================
export const authApi = {
	login: async (credentials) => {
		const { data } = await adminRequest('post', '/login', credentials)
		return data
	},

	getMe: async () => {
		const { data } = await adminRequest('get', '/me')
		return data
	},
}

// =====================================================
// METRICS
// =====================================================
export const metricsApi = {
	getOverview: async () => {
		const { data } = await adminRequest('get', '/metrics/overview')
		return data
	},

	getUserMetrics: async () => {
		const { data } = await adminRequest('get', '/metrics/users')
		return data
	},

	getClubMetrics: async () => {
		const { data } = await adminRequest('get', '/metrics/clubs')
		return data
	},

	getMatchMetrics: async () => {
		const { data } = await adminRequest('get', '/metrics/matches')
		return data
	},

	getGrowthData: async (days = 30) => {
		const { data } = await adminRequest('get', `/metrics/growth?days=${days}`)
		return data
	},

	getEngagement: async () => {
		const { data } = await adminRequest('get', '/metrics/engagement')
		return data
	},

	getRecentActivity: async (limit = 10) => {
		const { data } = await adminRequest('get', `/metrics/activity?limit=${limit}`)
		return data
	},
}

// =====================================================
// USERS
// =====================================================
export const usersApi = {
	list: async (params = {}) => {
		const { data } = await adminRequest('get', '/users', null, { params })
		return data
	},

	getById: async (id) => {
		const { data } = await adminRequest('get', `/users/${id}`)
		return data
	},

	update: async (id, userData) => {
		const { data } = await adminRequest('put', `/users/${id}`, userData)
		return data
	},

	delete: async (id) => {
		const { data } = await adminRequest('delete', `/users/${id}`)
		return data
	},

	updateRole: async (id, role) => {
		const { data } = await adminRequest('patch', `/users/${id}/role`, { role })
		return data
	},
}

// =====================================================
// CLUBS
// =====================================================
export const clubsApi = {
	list: async (params = {}) => {
		const { data } = await adminRequest('get', '/clubs', null, { params })
		return data
	},

	getById: async (id) => {
		const { data } = await adminRequest('get', `/clubs/${id}`)
		return data
	},

	update: async (id, clubData) => {
		const { data } = await adminRequest('put', `/clubs/${id}`, clubData)
		return data
	},

	delete: async (id) => {
		const { data } = await adminRequest('delete', `/clubs/${id}`)
		return data
	},

	getMembers: async (id, params = {}) => {
		const { data } = await adminRequest('get', `/clubs/${id}/members`, null, { params })
		return data
	},

	getMatches: async (id, params = {}) => {
		const { data } = await adminRequest('get', `/clubs/${id}/matches`, null, { params })
		return data
	},
}

// =====================================================
// MATCHES
// =====================================================
export const matchesApi = {
	list: async (params = {}) => {
		const { data } = await adminRequest('get', '/matches', null, { params })
		return data
	},

	getById: async (id) => {
		const { data } = await adminRequest('get', `/matches/${id}`)
		return data
	},

	update: async (id, matchData) => {
		const { data } = await adminRequest('put', `/matches/${id}`, matchData)
		return data
	},

	delete: async (id) => {
		const { data } = await adminRequest('delete', `/matches/${id}`)
		return data
	},
}

// =====================================================
// FEEDBACKS
// =====================================================
export const feedbacksApi = {
	list: async (params = {}) => {
		const { data } = await adminRequest('get', '/feedbacks', null, { params })
		return data
	},

	getById: async (id) => {
		const { data } = await adminRequest('get', `/feedbacks/${id}`)
		return data
	},

	respond: async (id, response) => {
		const { data } = await adminRequest('post', `/feedbacks/${id}/respond`, { response })
		return data
	},

	delete: async (id) => {
		const { data } = await adminRequest('delete', `/feedbacks/${id}`)
		return data
	},
}

// =====================================================
// RATINGS
// =====================================================
export const ratingsApi = {
	list: async (params = {}) => {
		const { data } = await adminRequest('get', '/ratings', null, { params })
		return data
	},

	getById: async (id) => {
		const { data } = await adminRequest('get', `/ratings/${id}`)
		return data
	},

	delete: async (id) => {
		const { data } = await adminRequest('delete', `/ratings/${id}`)
		return data
	},
}

// =====================================================
// INVITATIONS
// =====================================================
export const invitationsApi = {
	list: async (params = {}) => {
		const { data } = await adminRequest('get', '/invitations', null, { params })
		return data
	},

	getById: async (id) => {
		const { data } = await adminRequest('get', `/invitations/${id}`)
		return data
	},

	delete: async (id) => {
		const { data } = await adminRequest('delete', `/invitations/${id}`)
		return data
	},
}

// =====================================================
// BROADCAST
// =====================================================
export const broadcastApi = {
	getHistory: async (params = {}) => {
		const { data } = await adminRequest('get', '/broadcast', null, { params })
		return data
	},

	getPreview: async (broadcastData) => {
		const { data } = await adminRequest('post', '/broadcast/preview', broadcastData)
		return data
	},

	send: async (broadcastData) => {
		const { data } = await adminRequest('post', '/broadcast/send', broadcastData)
		return data
	},

	getById: async (id) => {
		const { data } = await adminRequest('get', `/broadcast/${id}`)
		return data
	},

	delete: async (id) => {
		const { data } = await adminRequest('delete', `/broadcast/${id}`)
		return data
	},
}

// =====================================================
// LOGS
// =====================================================
export const logsApi = {
	list: async (params = {}) => {
		const { data } = await adminRequest('get', '/logs', null, { params })
		return data
	},

	export: async (params = {}) => {
		const { data } = await adminRequest('get', '/logs/export', null, { params })
		return data
	},
}

// Export objeto legacy para compatibilidade
const adminApi = {
	request: adminRequest,
	auth: authApi,
	metrics: metricsApi,
	users: usersApi,
	clubs: clubsApi,
	matches: matchesApi,
	feedbacks: feedbacksApi,
	ratings: ratingsApi,
	invitations: invitationsApi,
	broadcast: broadcastApi,
	logs: logsApi,
}

export { adminApi, adminRequest, API_BASE_URL }
export default adminApi
